/**
 * iter198v (Feb 2026) — Vehicle make/model display helper.
 *
 * Background:
 *   The DVSA / DVLA lookup occasionally returns a make ("BMW") but not
 *   the specific model — in which case the intake fallback stores the
 *   literal placeholder string `"Unknown"`. Customers and FOH staff
 *   see "Bmw Unknown" on every job card, which looks broken.
 *
 *   The fix is two-pronged:
 *     (a) FOH gets an editable "Vehicle model" input on the intake
 *         create form so they can confirm/overwrite the DVSA value.
 *     (b) Every place that displays make+model renders through this
 *         helper, which:
 *           - Title-cases the make (DVSA returns "BMW" all-caps; we
 *             keep manufacturer brand casing where conventional —
 *             "BMW", "MG", "MINI", "AC", "VW" stay upper).
 *           - Treats "Unknown" / "" / null model as missing and falls
 *             back to make only.
 *
 * Public API:
 *   formatVehicleName(make, model)      -> "BMW 3 Series" | "BMW" | ""
 *   formatVehicleNameFromObject(obj)    -> same, accepting any object
 *                                          with .make / .model fields
 *                                          (handles vehicle, job, card,
 *                                          task.vehicle_make/model too)
 */

const ALWAYS_UPPER_MAKES = new Set([
  "BMW",
  "MG",
  "MINI",
  "AC",
  "VW",
  "DS",
  "GMC",
  "MX5",
  "DAF",
  "GAZ",
  "FSO",
  "ZAZ",
]);

const PLACEHOLDER_TOKENS = new Set(["unknown", "n/a", "na", "none", "-"]);

function _isMissing(value) {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  if (!s) return true;
  return PLACEHOLDER_TOKENS.has(s.toLowerCase());
}

function _normaliseMake(rawMake) {
  if (_isMissing(rawMake)) return "";
  const s = String(rawMake).trim();
  if (!s) return "";
  if (ALWAYS_UPPER_MAKES.has(s.toUpperCase())) return s.toUpperCase();
  // Title-case multi-word makes ("ALFA ROMEO" / "alfa romeo" → "Alfa Romeo").
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function formatVehicleName(make, model) {
  const m = _normaliseMake(make);
  if (_isMissing(model)) return m;
  const md = String(model).trim();
  return m ? `${m} ${md}` : md;
}

export function formatVehicleNameFromObject(obj) {
  if (!obj || typeof obj !== "object") return "";
  const make =
    obj.make
    ?? obj.vehicle_make
    ?? obj.manufacturer
    ?? (obj.vehicle && (obj.vehicle.make || obj.vehicle.vehicle_make))
    ?? "";
  const model =
    obj.model
    ?? obj.vehicle_model
    ?? (obj.vehicle && (obj.vehicle.model || obj.vehicle.vehicle_model))
    ?? "";
  return formatVehicleName(make, model);
}

export function hasUsableVehicleModel(model) {
  return !_isMissing(model);
}
